import { CURRENCIES } from '../i18n';

/**
 * Format an integer amount (in minor units or whole) to a localised currency string.
 * Tips/coins are stored as whole integers (e.g. 10 = £10 / $10 / ₹100).
 * For INR, divide by 1 (we treat coins as rupees directly).
 */
export function formatCurrency(amount, currency = 'INR', opts = {}) {
  const cur = CURRENCIES[currency] || CURRENCIES.INR;
  const { compact = false } = opts;

  try {
    if (compact && amount >= 1000) {
      const val = amount >= 100000
        ? (amount / 100000).toFixed(1) + (currency === 'INR' ? 'L' : 'K')  // lakh for INR
        : (amount / 1000).toFixed(1) + 'K';
      return cur.symbol + val;
    }
    return new Intl.NumberFormat(cur.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${cur.symbol}${amount}`;
  }
}

/**
 * Get just the currency symbol for a currency code.
 */
export function currencySymbol(currency = 'INR') {
  return (CURRENCIES[currency] || CURRENCIES.INR).symbol;
}

/**
 * Format a ticket price — shows "Free" when 0.
 */
export function formatTicketPrice(amount, currency = 'INR', freeLabel = 'Free') {
  if (!amount || amount === 0) return freeLabel;
  return formatCurrency(amount, currency);
}

// Approximate conversion rates (for display only, not for financial transactions).
// Real app would use a live FX API.
const FX_RATES = {
  GBP:1, USD:1.27, EUR:1.17, INR:105.8, CAD:1.72, AUD:1.96, JPY:189.5,
  SGD:1.71, AED:4.67, ZAR:23.5, BRL:6.4, MXN:21.8, NGN:2050, KES:163,
  IDR:19800, KRW:1690, GHS:19.5, CNY:9.1, NZD:2.13, TWD:40.5,
};

export function convertCurrency(amount, fromCurrency, toCurrency) {
  if (fromCurrency === toCurrency) return amount;
  const inGBP = amount / (FX_RATES[fromCurrency] || 1);
  return Math.round(inGBP * (FX_RATES[toCurrency] || 1));
}

export function getCurrencyInfo(currency) {
  return CURRENCIES[currency] || CURRENCIES.INR;
}

// Build CURRENCY_OPTIONS dynamically from the CURRENCIES map so it stays in sync
export const CURRENCY_OPTIONS = Object.entries(CURRENCIES).map(([code, c]) => ({
  value: code,
  label: `${c.flag} ${c.name} (${c.symbol})`,
  symbol: c.symbol,
}));
